import { useMutation, useQuery } from '@apollo/client';

import { faExternalLink, faPencil, faPlus, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import classnames from '../../../core/utils/classnames.tsx';
import * as React from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { withIcon } from '../../../core/components/Icon.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries.ts';
import { CREATE_CUSTOM_INTEGRATION, DELETE_CUSTOM_INTEGRATION, GET_CUSTOM_INTEGRATIONS } from '../../queries.ts';
import {
    CUSTOMER_INTEGRATION_CUSTOM_ADD_PATH,
    CUSTOMER_INTEGRATION_CUSTOM_EDIT_PATH,
    CUSTOMER_INTEGRATION_CUSTOM_PATH,
} from '../../constants.ts';
import { Controller, useForm } from 'react-hook-form';
import Button from '../../../core/components/Button.tsx';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { url } from '../../../core/utils/link.ts';
import FormField from '../../../core/components/FormField.tsx';
import CheckboxGroup from '../../../core/components/CheckboxGroup.tsx';
import Checkbox from '../../../core/components/Checkbox.tsx';
import Modal from '../../../core/components/Modal.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { Outlet } from 'react-router';
import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page/index.tsx';
import Table, { RowControlIconButton } from '../../../core/components/Table.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import { SecretField } from '../../../core/components/Secret.tsx';

interface CustomIntegrationFormModalOutletContext {
    integrations: any[];
    refetch: () => Promise<any>;
}

const AddIcon = withIcon(faPlus);
const EditIcon = withIcon(faPencil);
const DeleteIcon = withIcon(faTrashCan);
const ExternalLinkIcon = withIcon(faExternalLink);

const FORM_DEFAULT_VALUES = {
    name: '',
    enabled: true,
    baseUrl: '',
    authId: '',
    authToken: '',
    config: {} as Record<string, any>,
    extraData: {} as Record<string, any>,
    orderChannelIds: [],
    rfqChannelIds: [],
};

const CustomIntegrationFormModal = ({
    title,
    submitButtonLabel,
    onSubmit,
    onClose,
    integration,
}: {
    title: string;
    submitButtonLabel: string;
    onSubmit: (data: any) => void;
    onClose: () => void;
    integration?: any;
}) => {
    const { t } = useTranslation('integrations');

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    useEffect(() => {
        if (integration) {
            // Convert authToken from JSON object to string for display
            const authTokenString =
                integration.authToken && typeof integration.authToken === 'object'
                    ? JSON.stringify(integration.authToken)
                    : integration.authToken || '';

            // Parse config if it's a string
            let configValue = {};
            try {
                configValue = integration.config
                    ? typeof integration.config === 'string'
                        ? JSON.parse(integration.config)
                        : integration.config
                    : {};
            } catch (e) {
                configValue = {};
            }

            // Parse extraData if it's a string
            let extraDataValue = {};
            try {
                extraDataValue = integration.extraData
                    ? typeof integration.extraData === 'string'
                        ? JSON.parse(integration.extraData)
                        : integration.extraData
                    : {};
            } catch (e) {
                extraDataValue = {};
            }

            form.reset({
                ...FORM_DEFAULT_VALUES,
                name: integration.name,
                enabled: integration.enabled !== undefined ? integration.enabled : true,
                baseUrl: integration.baseUrl,
                authId: integration.authId || '',
                authToken: authTokenString,
                config: configValue,
                extraData: extraDataValue,
                orderChannelIds: integration.orderChannels.map((channel) => channel.id),
                rfqChannelIds: integration.rfqChannels.map((channel) => channel.id),
            });
        }
    }, [integration]);

    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
    });
    const orderChannels = channelData?.orderProcessingChannels || [];
    const rfqChannels = channelData?.rfqProcessingChannels || [];

    // Parse config for display (it comes as a string from GraphQL JSONString scalar)
    let parsedConfig;
    try {
        if (integration?.config) {
            parsedConfig = typeof integration.config === 'string' ? JSON.parse(integration.config) : integration.config;
        } else {
            parsedConfig = null;
        }
    } catch (e) {
        parsedConfig = null;
    }

    return (
        <Modal
            visible={true}
            onClose={onClose}
            title={title}
            buttons={
                <>
                    <Button type="button" asChild>
                        <Link to={url(CUSTOMER_INTEGRATION_CUSTOM_PATH)}>
                            {t('customIntegration.form.cancelButton')}
                        </Link>
                    </Button>
                    <Button variant="primary" type="submit" form="modal-form">
                        {submitButtonLabel}
                    </Button>
                </>
            }
        >
            <form className="flex flex-col gap-6" onSubmit={form.handleSubmit(onSubmit)} id="modal-form">
                <Controller
                    name="name"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('customIntegration.form.name')} error={formErrors.name}>
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="enabled"
                    control={form.control}
                    render={({ field }) => (
                        <label className="flex gap-3 items-start cursor-pointer">
                            <Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                            <div className="flex flex-col gap-1">
                                <span className="text-content-strong text-sm">
                                    {t('customIntegration.form.enabled')}
                                </span>
                                <span className="text-content-medium text-xs">
                                    {t('customIntegration.form.enabledDescription')}
                                </span>
                            </div>
                        </label>
                    )}
                />

                <Controller
                    name="baseUrl"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('customIntegration.form.baseUrl')} error={formErrors.baseUrl}>
                            <StringField {...field} placeholder={t('customIntegration.form.baseUrlPlaceholder')} />
                        </FormField>
                    )}
                />

                <Controller
                    name="authId"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('customIntegration.form.authId')} error={formErrors.authId}>
                            <StringField {...field} placeholder={t('customIntegration.form.authIdPlaceholder')} />
                        </FormField>
                    )}
                />

                <Controller
                    name="authToken"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('customIntegration.form.authToken')} error={formErrors.authToken}>
                            <SecretField
                                {...field}
                                secretName="authToken"
                                secretId={integration?.id}
                                defaultIsVisible={false}
                            />
                        </FormField>
                    )}
                />

                {integration && (
                    <FormField label={t('customIntegration.form.authState')}>
                        <StringField value={integration.authState || ''} readOnly={true} />
                    </FormField>
                )}

                {integration && parsedConfig && (
                    <FormField label={t('customIntegration.form.config')}>
                        <pre className="inline-block text-sm rounded bg-surface-strong p-2.5 text-content-strong">
                            {JSON.stringify(parsedConfig, null, 4)}
                        </pre>
                    </FormField>
                )}

                <Controller
                    name="orderChannelIds"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('customIntegration.form.orderChannels')} error={formErrors.orderChannelIds}>
                            <CheckboxGroup
                                {...field}
                                options={
                                    orderChannels.map((channel) => ({
                                        value: channel.id,
                                        label: channel.name,
                                    })) || []
                                }
                                onValueChange={(value) => field.onChange({ target: { value } })}
                            />
                        </FormField>
                    )}
                />

                <Controller
                    name="rfqChannelIds"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('customIntegration.form.rfqChannels')} error={formErrors.rfqChannelIds}>
                            <CheckboxGroup
                                {...field}
                                options={
                                    rfqChannels.map((channel) => ({
                                        value: channel.id,
                                        label: channel.name,
                                    })) || []
                                }
                                onValueChange={(value) => field.onChange({ target: { value } })}
                            />
                        </FormField>
                    )}
                />
            </form>
        </Modal>
    );
};

export const AddCustomIntegrationFormModal = () => {
    const { t } = useTranslation('integrations');
    const { refetch } = useOutletContext<CustomIntegrationFormModalOutletContext>();

    const { publishToast } = useToaster();
    const navigate = useNavigate();

    const [createIntegration] = useMutation(CREATE_CUSTOM_INTEGRATION);
    const handleCreateIntegration = async (formData) => {
        try {
            await createIntegration({
                variables: {
                    name: formData.name,
                    enabled: formData.enabled,
                    baseUrl: formData.baseUrl,
                    authId: formData.authId || null,
                    authToken: formData.authToken || null,
                    config: formData.config && Object.keys(formData.config).length > 0 ? formData.config : null,
                    orderChannels: formData.orderChannelIds,
                    rfqChannels: formData.rfqChannelIds,
                },
            });
            await refetch();
            publishToast({ description: t('customIntegration.createSuccess'), status: 'success' });
            navigate(url(CUSTOMER_INTEGRATION_CUSTOM_PATH));
        } catch (error) {
            console.error(error);
            publishToast({ description: t('customIntegration.createError'), status: 'error' });
        }
    };

    return (
        <CustomIntegrationFormModal
            title={t('customIntegration.form.createTitle')}
            submitButtonLabel={t('customIntegration.form.createButton')}
            onSubmit={handleCreateIntegration}
            onClose={() => navigate(url(CUSTOMER_INTEGRATION_CUSTOM_PATH))}
        />
    );
};

const CustomIntegrationRow = ({ integration, refetch }: { integration: any; refetch: any }) => {
    const { t } = useTranslation('integrations');
    const { publishToast } = useToaster();

    const [deleteIntegration] = useMutation(DELETE_CUSTOM_INTEGRATION);
    const handleDeleteIntegration = async () => {
        try {
            await deleteIntegration({ variables: { id: integration.id } });
            await refetch();
            publishToast({ description: t('customIntegration.deleteSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('customIntegration.deleteError'), status: 'error' });
        }
    };

    const orderChannels = integration ? [...integration.orderChannels] : [];
    const rfqChannels = integration ? [...integration.rfqChannels] : [];

    return (
        <>
            <Table.Row>
                <Table.Cell>{integration.name}</Table.Cell>
                <Table.Cell>
                    <span className={integration.enabled ? 'text-content-success-strong' : 'text-content-medium'}>
                        {integration.enabled
                            ? t('customIntegration.enabledStatus')
                            : t('customIntegration.disabledStatus')}
                    </span>
                </Table.Cell>
                <Table.Cell>{integration.baseUrl}</Table.Cell>
                <Table.Cell>
                    <span>
                        {t('customIntegration.authState', {
                            authState: integration.authState,
                        })}
                    </span>
                </Table.Cell>
                <Table.Cell>
                    <div className="flex gap-1.5 flex-wrap">
                        {orderChannels.map((channel) => (
                            <span
                                key={channel.id}
                                className="border-line-weak border border-solid bg-surface-strong flex gap-2 items-center px-1 rounded"
                            >
                                {channel.name}
                            </span>
                        ))}
                    </div>
                </Table.Cell>
                <Table.Cell>
                    <div className="flex gap-1.5 flex-wrap">
                        {rfqChannels.map((channel) => (
                            <span
                                key={channel.id}
                                className="border-line-weak border border-solid bg-surface-strong flex gap-2 items-center px-1 rounded"
                            >
                                {channel.name}
                            </span>
                        ))}
                    </div>
                </Table.Cell>
                <Table.Cell>{new Date(integration.createdAt).toLocaleString()}</Table.Cell>
                <Table.Cell sticky="right">
                    <div className="flex gap-2">
                        <RowControlIconButton asChild>
                            <Link
                                to={url(CUSTOMER_INTEGRATION_CUSTOM_EDIT_PATH, {
                                    integrationId: integration.id,
                                })}
                                className="!text-content-strong hover:!text-content-strong"
                            >
                                <EditIcon />
                            </Link>
                        </RowControlIconButton>

                        <RowControlIconButton
                            className="hover:bg-surface-danger-weak text-content-danger-strong hover:text-content-danger-strong"
                            onClick={handleDeleteIntegration}
                        >
                            <DeleteIcon />
                        </RowControlIconButton>
                    </div>
                </Table.Cell>
            </Table.Row>
        </>
    );
};

const CustomIntegrationPage = () => {
    const { t } = useTranslation('integrations');

    const {
        data: data,
        loading: loading,
        refetch: refetch,
    } = useQuery(GET_CUSTOM_INTEGRATIONS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
    });
    const integrations = data?.customIntegrations || [];

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1 min-w-0">
                <Page.Header className="justify-between">
                    <Page.HeaderTitle className="flex items-center gap-2">
                        <span>{t('customIntegration.title')}</span>
                    </Page.HeaderTitle>

                    <div className="flex gap-2">
                        <Page.HeaderButton asChild>
                            <a href={t('customIntegration.documentationLink')} target={'_blank'}>
                                <span className="flex gap-2 items-center">
                                    {t('customIntegration.documentation')}
                                    <ExternalLinkIcon />{' '}
                                </span>
                            </a>
                        </Page.HeaderButton>

                        <Page.HeaderButton asChild>
                            <Link to={url(CUSTOMER_INTEGRATION_CUSTOM_ADD_PATH)}>
                                <AddIcon />
                                {t('customIntegration.addButton')}
                            </Link>
                        </Page.HeaderButton>
                    </div>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6 relative">
                    <Table scrollable className={classnames('w-full table-auto', loading && 'opacity-50')}>
                        <Table.Head>
                            <Table.Row>
                                <Table.HeadCell className="min-w-96 w-96">
                                    {t('customIntegration.table.name')}
                                </Table.HeadCell>
                                <Table.HeadCell>{t('customIntegration.table.enabled')}</Table.HeadCell>
                                <Table.HeadCell>{t('customIntegration.table.baseUrl')}</Table.HeadCell>
                                <Table.HeadCell>{t('customIntegration.table.authState')}</Table.HeadCell>
                                <Table.HeadCell>{t('customIntegration.table.orderChannels')}</Table.HeadCell>
                                <Table.HeadCell>{t('customIntegration.table.rfqChannels')}</Table.HeadCell>
                                <Table.HeadCell>{t('customIntegration.table.createdAt')}</Table.HeadCell>
                                <Table.HeadCell sticky="right" className="min-w-14 w-14" />
                            </Table.Row>
                        </Table.Head>
                        <Table.Body>
                            {integrations && integrations.length > 0 ? (
                                integrations.map((integration) => (
                                    <CustomIntegrationRow
                                        key={integration.id}
                                        integration={integration}
                                        refetch={refetch}
                                    />
                                ))
                            ) : (
                                <Table.Row>
                                    <Table.Cell colSpan={8} className="text-center text-content-medium">
                                        {t('customIntegration.noIntegrations')}
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table>
                </Page.Content>
            </Page>

            <Outlet context={{ integrations, refetch }} />
        </Layout>
    );
};

export default CustomIntegrationPage;
